/**
 * Submit Title Customize Module V2
 * Uses localStorage and session manager for all data
 */
(function () {
  "use strict";

  // Run once per page (see submission-session-manager.js).
  if (window.submitTitleCustomizeLoaded) {
    return;
  }
  window.submitTitleCustomizeLoaded = true;

  // Step data can come back from the server through a resume link, so it is
  // untrusted: escape it before it reaches innerHTML or a selector.
  function esc(value) {
    return String(value === null || value === undefined ? "" : value)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;");
  }

  /** Escaped URL for src/href: only http(s) and same-site paths, else "#". */
  function safeUrl(value) {
    const url = String(value === null || value === undefined ? "" : value).trim();
    if (/[\\\s]/.test(url)) {
      return "#";
    }
    if (/^https?:\/\//i.test(url) || /^\/(?![\/\\])/.test(url)) {
      return esc(url);
    }
    return "#";
  }

  /** Value safe inside a quoted CSS attribute selector. */
  function cssAttr(value) {
    const text = String(value === null || value === undefined ? "" : value);
    return window.CSS && CSS.escape ? CSS.escape(text) : text.replace(/["\\]/g, "\\$&");
  }

  function getApplyBasePath() {
    return window.submissionApplyBasePath || "/partners/bloodstream/apply";
  }

  // State management
  let croppers = {
    poster: null,
    backdrop: null,
    treatment: null,
  };

  let uploadedImages = {
    poster: null,
    backdrop: null,
    still: null,
    treatment: null,
    logo: null,
  };

  const sessionManager = window.SubmissionSessionManager;

  /**
   * Initialize the module
   */
  function init() {
    // Only run on customize page
    const customizeContainer = document.getElementById(
      "submit-title-customize"
    );
    if (!customizeContainer) {
      return;
    }

    if (!sessionManager) {
      console.error("Session manager not loaded");
      return;
    }

    // Check if session was already submitted and redirect if so
    if (sessionManager.checkAndRedirectIfSubmitted()) {
      return; // Exit init - redirect is happening
    }

    console.log("[Customize] Initializing on customize page...");

    // Setup UI handlers first
    setupImageSelection();
    const specs = getAssetSpecs();
    const posterSpec = specs.poster || {};
    const backdropSpec = specs.backdrop || {};
    const stillSpec = specs.still || {};

    setupImageUpload(
      "poster",
      posterSpec.width && posterSpec.height
        ? posterSpec.width / posterSpec.height
        : null
    );
    setupImageUpload(
      "backdrop",
      backdropSpec.width && backdropSpec.height
        ? backdropSpec.width / backdropSpec.height
        : null
    );
    setupImageUpload(
      "still",
      stillSpec.width && stillSpec.height
        ? stillSpec.width / stillSpec.height
        : null
    );
    setupImageUpload("treatment", null);
    setupImageUpload("logo", null); // Logo - no cropping, preserve transparency
    setupFormSubmission();
    setupNavigation();
    setupHorizontalScroll();
    updateTitleInfo();
    updateProgress();

    // Load existing data from session AFTER setup
    // Use setTimeout to ensure DOM is fully ready
    setTimeout(() => {
      loadSessionData();
    }, 100);
  }

  function getAssetSpecs() {
    return (window.themeConfig && window.themeConfig.assetSpecs) || {};
  }

  /**
   * Load data from session storage
   */
  async function loadSessionData() {
    let step3Data = sessionManager.getStepData(3);

    // Check if we need to refresh expired URLs
    await refreshExpiredUrls(step3Data);

    // Re-fetch the data after refresh to get updated URLs
    step3Data = sessionManager.getStepData(3);

    console.log(
      "[Customize] Loading session data after URL refresh:",
      step3Data
    );

    // Restore selected images
    if (step3Data.selected_poster) {
      // Mark the image as selected. A poster the page no longer offers (smaller
      // than the poster size, POSTER-2X3-01) is dropped, not submitted.
      const posterOption = document.querySelector(
        `.image-option[data-type="poster"][data-url="${cssAttr(step3Data.selected_poster)}"]`
      );
      if (posterOption) {
        const posterInput = document.getElementById("selected_poster");
        if (posterInput) {
          posterInput.value = step3Data.selected_poster;
        }
        posterOption.classList.add("selected");
      } else {
        step3Data.selected_poster = "";
        sessionManager.saveStepData(3, step3Data);
      }
    }

    if (step3Data.selected_backdrop) {
      const backdropInput = document.getElementById("selected_backdrop");
      if (backdropInput) {
        backdropInput.value = step3Data.selected_backdrop;
      }

      // Mark the image as selected
      const backdropOption = document.querySelector(
        `.image-option[data-type="backdrop"][data-url="${cssAttr(step3Data.selected_backdrop)}"]`
      );
      if (backdropOption) {
        backdropOption.classList.add("selected");
      }
    }

    if (step3Data.selected_still) {
      const stillInput = document.getElementById("selected_still");
      if (stillInput) {
        stillInput.value = step3Data.selected_still;
      }

      // Mark the image as selected
      const stillOption = document.querySelector(
        `.image-option[data-type="still"][data-url="${cssAttr(step3Data.selected_still)}"]`
      );
      if (stillOption) {
        stillOption.classList.add("selected");
      }
    }

    // Restore custom uploads
    if (step3Data.custom_poster_url) {
      document.getElementById("custom_poster_url").value =
        step3Data.custom_poster_url;
      uploadedImages.poster = step3Data.custom_poster_data;
      if (step3Data.custom_poster_data) {
        showUploadedPreview("poster", step3Data.custom_poster_data);
      }
    }

    if (step3Data.custom_backdrop_url) {
      document.getElementById("custom_backdrop_url").value =
        step3Data.custom_backdrop_url;
      uploadedImages.backdrop = step3Data.custom_backdrop_data;
      console.log(
        "[Customize] Restored uploadedImages.backdrop from session:",
        uploadedImages.backdrop
      );
      if (step3Data.custom_backdrop_data) {
        showUploadedPreview("backdrop", step3Data.custom_backdrop_data);
      }
    }

    if (step3Data.custom_still_url) {
      document.getElementById("custom_still_url").value =
        step3Data.custom_still_url;
      uploadedImages.still = step3Data.custom_still_data;
      console.log(
        "[Customize] Restored uploadedImages.still from session:",
        uploadedImages.still
      );
      if (step3Data.custom_still_data) {
        showUploadedPreview("still", step3Data.custom_still_data);
      }
    }

    if (step3Data.custom_treatment_url) {
      console.log("[Customize] 🔄 Restoring treatment from session...");
      console.log(
        "[Customize] custom_treatment_url:",
        step3Data.custom_treatment_url
      );
      console.log(
        "[Customize] custom_treatment_data:",
        step3Data.custom_treatment_data
      );

      document.getElementById("custom_treatment_url").value =
        step3Data.custom_treatment_url;
      uploadedImages.treatment = step3Data.custom_treatment_data;
      console.log(
        "[Customize] uploadedImages.treatment set to:",
        uploadedImages.treatment
      );

      if (step3Data.custom_treatment_data) {
        console.log("[Customize] Showing treatment preview...");
        showUploadedPreview("treatment", step3Data.custom_treatment_data);
      } else {
        console.warn(
          "[Customize] ⚠️ custom_treatment_url exists but custom_treatment_data is missing!"
        );
      }
    } else {
      console.log("[Customize] No treatment data to restore");
    }

    // Restore logo selection
    if (step3Data.selected_logo) {
      const logoInput = document.getElementById("selected_logo");
      if (logoInput) {
        logoInput.value = step3Data.selected_logo;
      }

      // Mark the image as selected
      const logoOption = document.querySelector(
        `.image-option[data-type="logo"][data-url="${cssAttr(step3Data.selected_logo)}"]`
      );
      if (logoOption) {
        logoOption.classList.add("selected");
      }
    }

    // Restore custom logo upload
    if (step3Data.custom_logo_url) {
      const customLogoInput = document.getElementById("custom_logo_url");
      if (customLogoInput) {
        customLogoInput.value = step3Data.custom_logo_url;
      }
      uploadedImages.logo = step3Data.custom_logo_data;
      if (step3Data.custom_logo_data) {
        showUploadedPreview("logo", step3Data.custom_logo_data);
      }
    }

    // Log what was restored to help debug
    console.log("[Customize] Session data restored. uploadedImages state:", {
      poster: uploadedImages.poster,
      backdrop: uploadedImages.backdrop,
      still: uploadedImages.still,
      treatment: uploadedImages.treatment,
      logo: uploadedImages.logo,
    });
  }

  /**
   * Refresh expired signed URLs
   */
  async function refreshExpiredUrls(step3Data) {
    // Check if any custom image data has expired URLs
    const needsRefresh = [];
    const now = new Date();

    // Check each image type
    ["poster", "backdrop", "still", "treatment", "logo"].forEach((type) => {
      const customData = step3Data[`custom_${type}_data`];
      if (customData && customData.expires_at) {
        const expiresAt = new Date(customData.expires_at);
        // Refresh if expired or will expire in next 5 minutes
        if (expiresAt <= new Date(now.getTime() + 5 * 60000)) {
          needsRefresh.push(type);
          console.log(
            `[Customize] ${type} URL expired or expiring soon, needs refresh`
          );
        }
      }
    });

    // If nothing needs refresh, return
    if (needsRefresh.length === 0) {
      console.log("[Customize] All URLs are still valid");
      return;
    }

    console.log("[Customize] Refreshing expired URLs for:", needsRefresh);

    try {
      // Call API to refresh URLs
      const response = await fetch("/api/submissions/refresh-urls", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          uuid: sessionManager.getSessionId(),
          step: 3,
        }),
      });

      const result = await response.json();

      if (!result.success) {
        console.error("[Customize] Failed to refresh URLs:", result.message);
        return;
      }

      const refreshedUrls = result.data.refreshed_urls || {};
      console.log("[Customize] Received refreshed URLs:", refreshedUrls);

      // Update step3Data with new URLs
      Object.keys(refreshedUrls).forEach((key) => {
        // Map keys like 'poster_url' to 'custom_poster_data.url'
        const match = key.match(/^(\w+)_url$/);
        if (match) {
          const type = match[1];
          const dataKey = `custom_${type}_data`;

          if (step3Data[dataKey]) {
            // Update the URL in the data object
            step3Data[dataKey].url = refreshedUrls[key];
            // Update expires_at if provided
            if (refreshedUrls[`${type}_expires_at`]) {
              step3Data[dataKey].expires_at =
                refreshedUrls[`${type}_expires_at`];
            }
            console.log(`[Customize] Updated ${type} URL:`, refreshedUrls[key]);
          }
        }
      });

      // Save updated data back to session
      sessionManager.saveStepData(3, step3Data);
      console.log("[Customize] URLs refreshed and saved to session");
    } catch (error) {
      console.error("[Customize] Error refreshing URLs:", error);
    }
  }

  /**
   * Update title information card
   */
  function updateTitleInfo() {
    // Get title info from previous steps
    const step1Data = sessionManager.getStepData(1);

    if (step1Data.title_name) {
      const decodedTitle = window.HtmlEntityDecoder
        ? window.HtmlEntityDecoder.decode(step1Data.title_name)
        : step1Data.title_name;
      document.getElementById("title-name").textContent = decodedTitle;
    }

    if (step1Data.year) {
      document.getElementById(
        "title-year"
      ).textContent = `Year: ${step1Data.year}`;
    }

    if (step1Data.genres) {
      document.getElementById(
        "title-genres"
      ).textContent = `Genres: ${step1Data.genres}`;
    }

    if (step1Data.poster_url) {
      const posterDiv = document.getElementById("title-poster");
      const posterImg = document.getElementById("title-poster-img");
      if (posterDiv && posterImg) {
        posterImg.src = step1Data.poster_url;
        posterDiv.classList.remove("hide");
      }
    }
  }

  /**
   * Update progress bar
   */
  function updateProgress() {
    const progress = sessionManager.getProgress();
    const progressBar = document.getElementById("progress-bar");
    if (progressBar) {
      // At step 3, we should be at 75% progress
      progressBar.style.width = "75%";
    }
  }

  /**
   * Setup image selection handlers for IMDB/TMDB images
   */
  function setupImageSelection() {
    const imageOptions = document.querySelectorAll(".image-option");

    imageOptions.forEach((option) => {
      option.addEventListener("click", function () {
        const type = this.dataset.type;
        const url = this.dataset.url;

        // Remove selected class from siblings of same type
        document
          .querySelectorAll(`.image-option[data-type="${type}"]`)
          .forEach((opt) => {
            opt.classList.remove("selected");
          });

        // Add selected class to clicked option
        this.classList.add("selected");

        // Update hidden input
        const input = document.getElementById(`selected_${type}`);
        if (input) {
          input.value = url;
        }

        // Clear custom upload if selecting TMDB/IMDB image
        if (!url.startsWith("upload:")) {
          const customInput = document.getElementById(`custom_${type}_url`);
          if (customInput) {
            customInput.value = "";
          }
        }

        // If poster is selected, update sidebar poster and save to step 1 data
        if (type === "poster") {
          updateSidebarPoster(url);

          // Save to step 1 data so it persists across all steps
          const step1Data = sessionManager.getStepData(1);
          step1Data.poster_url = url;
          sessionManager.saveStepData(1, step1Data);

          console.log(`[Customize] Updated poster in step 1 data:`, url);
        }

        console.log(`[Customize] Selected ${type}:`, url);
      });
    });
  }

  /**
   * Update the sidebar poster image
   */
  function updateSidebarPoster(posterUrl) {
    const posterDiv = document.getElementById("title-poster");
    const posterImg = document.getElementById("title-poster-img");

    if (posterDiv && posterImg) {
      posterImg.src = posterUrl;
      posterDiv.classList.remove("hide");

      // Handle image load/error
      posterImg.onload = () => {
        console.log("[Customize] Sidebar poster updated successfully");
      };

      posterImg.onerror = () => {
        console.error("[Customize] Failed to load sidebar poster");
        posterDiv.classList.add("hide");
      };
    }
  }

  /**
   * Setup image upload and crop functionality
   *
   * @param {string} type - 'poster' or 'backdrop'
   * @param {number} aspectRatio - Crop aspect ratio
   */
  function setupImageUpload(type, aspectRatio) {
    const fileInput = document.getElementById(`${type}-upload`);
    const uploadDrop = document.getElementById(`${type}-upload-drop`);
    const preview = document.getElementById(`${type}-preview`);

    if (!fileInput || !uploadDrop || !preview) {
      return;
    }

    // Prevent duplicate event listener attachment
    if (uploadDrop.dataset.initialized === "true") {
      console.log(
        `[Customize] Upload already initialized for ${type}, skipping`
      );
      return;
    }
    uploadDrop.dataset.initialized = "true";

    // Click drop zone to trigger file input
    uploadDrop.addEventListener("click", (e) => {
      console.log(
        `[Customize] Drop zone clicked for ${type}`,
        e.target,
        "isTrusted:",
        e.isTrusted
      );

      // Only process user-initiated clicks (not synthetic clicks from fileInput.click())
      if (!e.isTrusted) {
        console.log(`[Customize] Click ignored - synthetic event`);
        return;
      }

      // Check if the click is on the file input itself (prevent loop)
      if (e.target === fileInput || e.target.closest('input[type="file"]')) {
        console.log(`[Customize] Click ignored - file input itself`);
        return;
      }

      // Check if clicking on a button or control
      if (e.target.closest(".crop-controls") || e.target.closest("button")) {
        console.log(`[Customize] Click ignored - button or control`);
        return;
      }

      e.stopPropagation();
      e.preventDefault();

      console.log(`[Customize] Triggering file input for ${type}`);
      fileInput.click();
    });

    // Drag and drop handlers
    uploadDrop.addEventListener("dragover", (e) => {
      e.preventDefault();
      uploadDrop.classList.add("drag-over");
    });

    uploadDrop.addEventListener("dragleave", () => {
      uploadDrop.classList.remove("drag-over");
    });

    uploadDrop.addEventListener("drop", (e) => {
      e.preventDefault();
      uploadDrop.classList.remove("drag-over");

      const file = e.dataTransfer.files[0];
      if (file) {
        handleFileSelect(file, type, aspectRatio);
      }
    });

    // Handle file selection
    fileInput.addEventListener("change", function (e) {
      const file = e.target.files[0];
      if (file) {
        handleFileSelect(file, type, aspectRatio);
      }
    });
  }

  /**
   * Handle file selection
   *
   * @param {File} file - Selected file
   * @param {string} type - 'poster', 'backdrop', 'treatment', or 'logo'
   * @param {number|null} aspectRatio - Crop aspect ratio (null = no cropping)
   */
  function handleFileSelect(file, type, aspectRatio) {
    // Validate file type
    if (!file.type.match("image.*")) {
      showNotification("Please select an image file", "error");
      return;
    }

    // For logos, enforce PNG only
    if (type === "logo" && file.type !== "image/png") {
      showNotification(
        "Logos must be PNG format to preserve transparency",
        "error"
      );
      return;
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      showNotification(
        "Image file is too large. Maximum size is 10MB",
        "error"
      );
      return;
    }

    // Read and display the image
    const reader = new FileReader();
    reader.onload = function (event) {
      console.log(
        `[Customize] File loaded for type: ${type}, aspectRatio: ${aspectRatio}`
      );

      // Logos need transparency validation
      if (type === "logo") {
        console.log(
          "[Customize] This is a logo upload - validating transparency..."
        );
        validatePNGTransparency(event.target.result, function (
          hasTransparency
        ) {
          if (!hasTransparency) {
            console.warn(
              "[Customize] ⚠️ Logo rejected - no transparency detected"
            );
            showNotification(
              "Logo must have a transparent background. Please upload a PNG with transparency.",
              "error"
            );
            // Reset file input
            document.getElementById(`${type}-upload`).value = "";
            return;
          }
          console.log(
            "[Customize] ✅ Logo approved - transparency detected, saving..."
          );
          saveImageWithoutCropping(type, event.target.result, file.name, file);
        });
      } else if (type === "treatment") {
        const specs = getAssetSpecs();
        const tSpec = specs.treatment || {};

        console.log(
          "[Customize] This is a treatment upload - validating dimensions..."
        );

        if (tSpec.exact_dimensions && tSpec.width && tSpec.height) {
          validateImageDimensions(
            event.target.result,
            tSpec.width,
            tSpec.height,
            function (isValid, actualWidth, actualHeight) {
              if (!isValid) {
                showNotification(
                  `Title treatment must be exactly ${tSpec.width} × ${tSpec.height} pixels. Your image is ${actualWidth} × ${actualHeight} pixels.`,
                  "error"
                );
                document.getElementById(`${type}-upload`).value = "";
                return;
              }
              saveImageWithoutCropping(
                type,
                event.target.result,
                file.name,
                file
              );
            }
          );
        } else if (tSpec.width && tSpec.height) {
          validateMaxDimensions(
            event.target.result,
            tSpec.width,
            tSpec.height,
            function (isValid, actualWidth, actualHeight) {
              if (!isValid) {
                showNotification(
                  `Title treatment must be at most ${tSpec.width} × ${tSpec.height} pixels. Your image is ${actualWidth} × ${actualHeight} pixels.`,
                  "error"
                );
                document.getElementById(`${type}-upload`).value = "";
                return;
              }
              saveImageWithoutCropping(
                type,
                event.target.result,
                file.name,
                file
              );
            }
          );
        } else {
          saveImageWithoutCropping(type, event.target.result, file.name, file);
        }
      } else if (aspectRatio === null) {
        // Other types that skip cropping
        console.log(
          `[Customize] Saving ${type} without cropping (no aspect ratio)...`
        );
        saveImageWithoutCropping(type, event.target.result, file.name, file);
      } else {
        // Poster, backdrop, still — validate minimum dimensions before cropping
        const minDims = getMinDimensions(type);
        if (minDims) {
          validateMinimumDimensions(
            event.target.result,
            minDims.width,
            minDims.height,
            function (isValid, actualWidth, actualHeight) {
              if (!isValid) {
                const typeLabel = type === "poster" ? "Poster" : type === "backdrop" ? "Banner" : "Backdrop/Hero";
                showNotification(
                  `${typeLabel} image is too small. Minimum size is ${minDims.width} × ${minDims.height} pixels. Your image is ${actualWidth} × ${actualHeight} pixels.`,
                  "error"
                );
                document.getElementById(`${type}-upload`).value = "";
                return;
              }
              initCropper(type, event.target.result, file.name, aspectRatio);
            }
          );
        } else {
          initCropper(type, event.target.result, file.name, aspectRatio);
        }
      }
    };
    reader.readAsDataURL(file);
  }

  /**
   * Validate that a PNG image has transparency
   *
   * @param {string} dataURL - Base64 data URL of the image
   * @param {Function} callback - Callback function with (hasTransparency) boolean
   */
  function validatePNGTransparency(dataURL, callback) {
    console.log("[Customize] 🔍 Validating PNG transparency...");

    const img = new Image();
    img.onload = function () {
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      canvas.width = img.width;
      canvas.height = img.height;

      console.log(`[Customize] 📐 Image loaded: ${img.width}x${img.height}`);

      // Draw the image
      ctx.drawImage(img, 0, 0);

      // Get pixel data
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const pixels = imageData.data;

      const totalPixels = pixels.length / 4;
      console.log(`[Customize] 🎨 Total pixels to check: ${totalPixels}`);

      // Check if any pixel has transparency (alpha < 255)
      let hasTransparency = false;
      let transparentPixelCount = 0;
      for (let i = 3; i < pixels.length; i += 4) {
        if (pixels[i] < 255) {
          hasTransparency = true;
          transparentPixelCount++;
        }
      }

      const transparencyPercent = (
        (transparentPixelCount / totalPixels) *
        100
      ).toFixed(2);
      console.log(
        `[Customize] ✨ Transparent pixels: ${transparentPixelCount} (${transparencyPercent}%)`
      );
      console.log(
        `[Customize] ${
          hasTransparency ? "✅ HAS transparency" : "❌ NO transparency"
        }`
      );

      callback(hasTransparency);
    };
    img.onerror = function (error) {
      console.error(
        "[Customize] ❌ Error loading image for transparency check:",
        error
      );
      callback(false);
    };
    img.src = dataURL;
  }

  /**
   * Validate that an image has exact dimensions
   *
   * @param {string} dataURL - Base64 data URL of the image
   * @param {number} requiredWidth - Required width in pixels
   * @param {number} requiredHeight - Required height in pixels
   * @param {Function} callback - Callback function with (isValid, actualWidth, actualHeight)
   */
  function validateImageDimensions(
    dataURL,
    requiredWidth,
    requiredHeight,
    callback
  ) {
    console.log(
      `[Customize] 🔍 Validating image dimensions (required: ${requiredWidth}x${requiredHeight})...`
    );

    const img = new Image();
    img.onload = function () {
      const isValid =
        img.width === requiredWidth && img.height === requiredHeight;
      console.log(
        `[Customize] 📐 Image dimensions: ${img.width}x${img.height}`
      );
      console.log(
        `[Customize] ${
          isValid ? "✅ VALID dimensions" : "❌ INVALID dimensions"
        }`
      );

      callback(isValid, img.width, img.height);
    };
    img.onerror = function (error) {
      console.error(
        "[Customize] ❌ Error loading image for dimension check:",
        error
      );
      callback(false, 0, 0);
    };
    img.src = dataURL;
  }

  function validateMaxDimensions(dataURL, maxWidth, maxHeight, callback) {
    console.log(
      `[Customize] Validating max dimensions (max: ${maxWidth}x${maxHeight})...`
    );

    const img = new Image();
    img.onload = function () {
      const isValid = img.width <= maxWidth && img.height <= maxHeight;
      console.log(`[Customize] Image dimensions: ${img.width}x${img.height}`);
      console.log(
        `[Customize] ${
          isValid ? "VALID" : "INVALID"
        } (max: ${maxWidth}x${maxHeight})`
      );
      callback(isValid, img.width, img.height);
    };
    img.onerror = function (error) {
      console.error(
        "[Customize] Error loading image for dimension check:",
        error
      );
      callback(false, 0, 0);
    };
    img.src = dataURL;
  }

  /**
   * Get minimum dimensions for a given image type.
   * Source images must be at least this large to produce usable output.
   */
  function getMinDimensions(type) {
    switch (type) {
      case "poster":
        // The theme's poster size (2000 x 3000, POSTER-2X3-01): never enlarged to it.
        return posterSize();
      case "backdrop":
        return { width: 1280, height: 720 };
      case "still":
        return { width: 1280, height: 720 };
      default:
        return null;
    }
  }

  function posterSize() {
    const spec = getAssetSpecs().poster || {};
    return spec.width && spec.height
      ? { width: spec.width, height: spec.height }
      : { width: 480, height: 720 };
  }

  /**
   * Validate that an image meets minimum dimension requirements.
   * Checks that at least one axis is large enough for the target output
   * (since the cropper can handle aspect-ratio adjustment).
   */
  function validateMinimumDimensions(dataURL, minWidth, minHeight, callback) {
    const img = new Image();
    img.onload = function () {
      // Both dimensions must be at least the minimum
      const isValid = img.width >= minWidth && img.height >= minHeight;
      callback(isValid, img.width, img.height);
    };
    img.onerror = function () {
      callback(false, 0, 0);
    };
    img.src = dataURL;
  }

  /**
   * Save image without cropping (for logos and treatment)
   * Uploads to server instead of storing in localStorage
   *
   * @param {string} type - Image type
   * @param {string} base64data - Base64 data URL
   * @param {string} filename - Original filename
   * @param {File} file - Original file object
   */
  function saveImageWithoutCropping(type, base64data, filename, file) {
    console.log(`[Customize] 💾 saveImageWithoutCropping called for ${type}`);
    console.log(
      `[Customize] 📤 Uploading to server instead of localStorage...`
    );

    // Get session ID
    const sessionId = sessionManager.getSessionId();
    if (!sessionId) {
      console.error("[Customize] ❌ No session ID available");
      showNotification("Session error. Please refresh the page.", "error");
      return;
    }

    // Prepare form data for upload
    const formData = new FormData();
    formData.append("submission_uuid", sessionId);
    formData.append("type", type);
    formData.append("image", base64data);
    formData.append("filename", filename);

    // Upload to server
    fetch("/api/submissions/upload-image", {
      method: "POST",
      body: formData,
    })
      .then((response) => response.json())
      .then((data) => {
        console.log(`[Customize] 📥 Upload response:`, data);

        if (!data.success) {
          throw new Error(data.message || "Upload failed");
        }

        // Create image reference object (NO base64 data!)
        const imageData = {
          upload_id: data.data.upload_id,
          submission_upload_id: data.data.submission_upload_id,
          url: data.data.url, // Signed URL from server
          filename: data.data.filename,
          type: type,
          size: data.data.size,
          width: data.data.width,
          height: data.data.height,
          uploaded_at: new Date().toISOString(),
          expires_at: data.data.expires_at,
        };

        console.log(`[Customize] ✅ Image uploaded successfully:`, {
          upload_id: imageData.upload_id,
          filename: imageData.filename,
          type: imageData.type,
          size: imageData.size,
        });

        // Store ONLY the reference in session (not the base64 data)
        const stepData = sessionManager.getStepData(3);
        stepData[`custom_${type}_url`] = `upload:${imageData.upload_id}`;
        stepData[`custom_${type}_data`] = imageData;
        stepData[`selected_${type}`] = ""; // Clear TMDB selection

        console.log(
          `[Customize] 💾 Saving reference to session - custom_${type}_url:`,
          stepData[`custom_${type}_url`]
        );

        sessionManager.saveStepData(3, stepData);

        // Update UI
        uploadedImages[type] = imageData;
        console.log(
          `[Customize] 📝 Updated uploadedImages.${type}:`,
          uploadedImages[type]
        );

        const hiddenInput = document.getElementById(`custom_${type}_url`);
        if (hiddenInput) {
          hiddenInput.value = `upload:${imageData.upload_id}`;
          console.log(
            `[Customize] 📝 Updated hidden input custom_${type}_url:`,
            hiddenInput.value
          );
        }

        // If poster is uploaded, update sidebar poster and save to step 1 data
        if (type === "poster") {
          updateSidebarPoster(imageData.url);

          // Save to step 1 data so it persists across all steps
          const step1Data = sessionManager.getStepData(1);
          step1Data.poster_url = imageData.url;
          sessionManager.saveStepData(1, step1Data);

          console.log(
            "[Customize] Updated poster in step 1 data with custom upload"
          );
        }

        // Show uploaded preview
        showUploadedPreview(type, imageData);

        // Reset file input to allow re-upload
        const fileInput = document.getElementById(`${type}-upload`);
        if (fileInput) {
          fileInput.value = "";
        }
      })
      .catch((error) => {
        console.error(`[Customize] ❌ Upload failed:`, error);
        showNotification(`Failed to upload ${type}: ${error.message}`, "error");
      });
  }

  /**
   * Initialize Cropper.js for image cropping
   *
   * @param {string} type - 'poster' or 'backdrop'
   * @param {string} imageSrc - Image data URL
   * @param {string} filename - Original filename
   * @param {number} aspectRatio - Crop aspect ratio
   */
  function initCropper(type, imageSrc, filename, aspectRatio) {
    console.log(
      `[Customize] 🎬 Initializing cropper for ${type}, aspect ratio: ${aspectRatio}`
    );

    // Get modal elements
    const modal = document.getElementById("crop-modal");
    const modalTitle = document.getElementById("crop-modal-title");
    const modalBody = document.getElementById("crop-modal-body");
    const modalControls = document.getElementById("crop-modal-controls");
    const modalClose = document.getElementById("crop-modal-close");

    if (!modal) {
      console.error("[Customize] ❌ Crop modal not found!");
      return;
    }

    console.log("[Customize] ✅ Modal elements found, setting up cropper...");

    // Destroy existing cropper if any
    if (croppers[type]) {
      croppers[type].destroy();
      croppers[type] = null;
    }

    // Clear modal body
    modalBody.innerHTML = "";
    modalControls.innerHTML = "";

    // Set modal title
    modalTitle.textContent = `Crop ${
      type.charAt(0).toUpperCase() + type.slice(1)
    } Image`;

    // Create image element
    const img = document.createElement("img");
    img.src = imageSrc;
    img.id = `crop-image-${type}`;

    // Create controls
    modalControls.innerHTML = `
      <button type="button" class="button success has-icon" id="${type}-crop-save">
        <i class="fas fa-check"></i>
        Crop & Save
      </button>
      <button type="button" class="button secondary has-icon" id="${type}-crop-cancel">
        <i class="fas fa-times"></i>
        Cancel
      </button>
    `;

    modalBody.appendChild(img);

    // Show modal
    modal.classList.add("active");

    // Initialize Cropper.js
    croppers[type] = new Cropper(img, {
      aspectRatio: aspectRatio,
      viewMode: 1,
      autoCropArea: 1,
      responsive: true,
      background: false,
      zoomable: true,
      rotatable: true,
      cropBoxResizable: true,
      cropBoxMovable: true,
      guides: true,
      center: true,
      highlight: true,
      dragMode: "move",
    });

    // Handle crop & save
    document
      .getElementById(`${type}-crop-save`)
      .addEventListener("click", () => {
        cropAndSaveImage(type, filename);
      });

    // Handle cancel
    const cancelHandler = () => {
      closeCropModal(type);
      document.getElementById(`${type}-upload`).value = "";
    };

    document
      .getElementById(`${type}-crop-cancel`)
      .addEventListener("click", cancelHandler);
    modalClose.addEventListener("click", cancelHandler);

    // Close on background click
    modal.addEventListener("click", (e) => {
      if (e.target === modal) {
        cancelHandler();
      }
    });
  }

  /**
   * Close crop modal
   *
   * @param {string} type - 'poster' or 'backdrop'
   */
  function closeCropModal(type) {
    const modal = document.getElementById("crop-modal");
    modal.classList.remove("active");

    if (croppers[type]) {
      croppers[type].destroy();
      croppers[type] = null;
    }
  }

  /**
   * Crop and save the image
   *
   * @param {string} type - 'poster' or 'backdrop'
   * @param {string} filename - Original filename
   */
  function cropAndSaveImage(type, filename) {
    if (!croppers[type]) {
      showNotification("No image to crop", "error");
      return;
    }

    // Get cropped canvas with exact dimensions
    let width, height;
    if (type === "poster") {
      ({ width, height } = posterSize());
      // The poster is cut from at least its own size of the picture, never enlarged.
      const box = croppers[type].getData();
      if (box.width < width - 1 || box.height < height - 1) {
        showNotification(
          `This crop is ${Math.round(box.width)} × ${Math.round(box.height)} pixels of the picture. The poster needs at least ${width} × ${height}. Zoom out or make the crop box larger.`,
          "error"
        );
        return;
      }
    } else if (type === "treatment") {
      width = 1920;
      height = 1080;
    } else {
      // backdrop or still
      width = 1280;
      height = 720;
    }

    const canvas = croppers[type].getCroppedCanvas({
      width: width,
      height: height,
      fillColor: type === "treatment" ? "transparent" : "#000",
      imageSmoothingEnabled: true,
      imageSmoothingQuality: "high",
    });

    if (!canvas) {
      showNotification("Failed to crop image", "error");
      return;
    }

    // Convert canvas to blob and upload to server
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          showNotification("Failed to process image", "error");
          return;
        }

        console.log(
          `[Customize] 🎬 Cropped ${type} to ${width}x${height}, uploading to server...`
        );

        // Convert to base64 for upload
        const reader = new FileReader();
        reader.onloadend = function () {
          const base64data = reader.result;

          // Get session ID
          const sessionId = sessionManager.getSessionId();
          if (!sessionId) {
            console.error("[Customize] ❌ No session ID available");
            showNotification(
              "Session error. Please refresh the page.",
              "error"
            );
            return;
          }

          // Prepare form data for upload
          const formData = new FormData();
          formData.append("submission_uuid", sessionId);
          formData.append("type", type);
          formData.append("image", base64data);
          formData.append("filename", filename);

          // Upload to server
          fetch("/api/submissions/upload-image", {
            method: "POST",
            body: formData,
          })
            .then((response) => response.json())
            .then((data) => {
              console.log(`[Customize] 📥 Upload response:`, data);

              if (!data.success) {
                throw new Error(data.message || "Upload failed");
              }

              // Create image reference object (NO base64 data!)
              const imageData = {
                upload_id: data.data.upload_id,
                submission_upload_id: data.data.submission_upload_id,
                url: data.data.url, // Signed URL from server
                filename: data.data.filename,
                type: type,
                size: data.data.size,
                width: data.data.width,
                height: data.data.height,
                uploaded_at: new Date().toISOString(),
                expires_at: data.data.expires_at,
              };

              console.log(`[Customize] ✅ Image uploaded successfully:`, {
                upload_id: imageData.upload_id,
                filename: imageData.filename,
                type: imageData.type,
                size: imageData.size,
              });

              // Store ONLY the reference in session (not the base64 data)
              const stepData = sessionManager.getStepData(3);
              stepData[`custom_${type}_url`] = `upload:${imageData.upload_id}`;
              stepData[`custom_${type}_data`] = imageData;
              stepData[`selected_${type}`] = ""; // Clear TMDB selection
              sessionManager.saveStepData(3, stepData);

              // Update UI
              uploadedImages[type] = imageData;
              console.log(`[Customize] Set uploadedImages.${type}:`, imageData);
              console.log(
                `[Customize] Full uploadedImages object:`,
                uploadedImages
              );
              document.getElementById(
                `custom_${type}_url`
              ).value = `upload:${imageData.upload_id}`;

              // If poster is uploaded, update sidebar poster and save to step 1 data
              if (type === "poster") {
                updateSidebarPoster(imageData.url);

                // Save to step 1 data so it persists across all steps
                const step1Data = sessionManager.getStepData(1);
                step1Data.poster_url = imageData.url;
                sessionManager.saveStepData(1, step1Data);

                console.log(
                  "[Customize] Updated poster in step 1 data with custom cropped upload"
                );
              }

              // Show uploaded preview
              showUploadedPreview(type, imageData);

              // Close modal
              closeCropModal(type);

              // Reset file input to allow re-upload
              const fileInput = document.getElementById(`${type}-upload`);
              if (fileInput) {
                fileInput.value = "";
              }
            })
            .catch((error) => {
              console.error(`[Customize] ❌ Upload failed:`, error);
              showNotification(
                `Failed to upload ${type}: ${error.message}`,
                "error"
              );
              closeCropModal(type);
            });
        };
        reader.readAsDataURL(blob);
      },
      type === "treatment" ? "image/png" : "image/jpeg",
      type === "treatment" ? 1.0 : 0.92
    );
  }

  /**
   * Show uploaded image preview
   *
   * @param {string} type - 'poster', 'backdrop', 'treatment', or 'logo'
   * @param {object} imageData - Uploaded image data
   */
  function showUploadedPreview(type, imageData) {
    const uploadDrop = document.getElementById(`${type}-upload-drop`);

    // Special handling for treatment - display below upload zone
    if (type === "treatment") {
      const previewContainer = document.getElementById(
        "treatment-uploaded-preview"
      );
      if (previewContainer) {
        previewContainer.innerHTML = `
          <img src="${safeUrl(imageData.url)}" alt="Uploaded Title Treatment" loading="lazy">
          <div style="text-align: center; margin-top: 0.5rem; display: flex; align-items: center; justify-content: center; gap: 1rem;">
            <small class="text-muted">Title Treatment Uploaded</small>
            <button type="button" class="button tiny alert" id="remove-treatment-btn">
              <i class="fas fa-times"></i> Remove
            </button>
          </div>
        `;
        previewContainer.classList.remove("hide");

        // Add remove button handler
        const removeBtn = document.getElementById("remove-treatment-btn");
        if (removeBtn) {
          removeBtn.addEventListener("click", function () {
            // Clear the preview
            previewContainer.innerHTML = "";
            previewContainer.classList.add("hide");

            // Clear the uploaded data
            uploadedImages.treatment = null;
            const customInput = document.getElementById("custom_treatment_url");
            if (customInput) {
              customInput.value = "";
            }

            // Clear from session storage
            const stepData = sessionManager.getStepData(3) || {};
            stepData.custom_treatment_url = "";
            stepData.custom_treatment_data = null;
            sessionManager.saveStepData(3, stepData);

            console.log("[Customize] Treatment removed");
            showNotification("Title treatment removed", "info");
          });
        }
      }

      // Update hidden input
      const customInput = document.getElementById("custom_treatment_url");
      if (customInput) {
        customInput.value = `upload:${imageData.upload_id}`;
      }

      console.log("[Customize] Treatment preview displayed below upload zone");
      return;
    }

    // Standard handling for other types (poster, backdrop, logo)
    const imageGrid = uploadDrop.closest(".image-grid");

    // Remove existing uploaded item if any
    const existingItem = document.getElementById(`${type}-uploaded-item`);
    if (existingItem) {
      existingItem.remove();
    }

    // Create new image grid item with uploaded image
    const gridItem = document.createElement("div");
    gridItem.className = "image-grid-item";
    gridItem.id = `${type}-uploaded-item`;
    gridItem.innerHTML = `
      <div class="image-option selected" data-type="${type}" data-url="upload:${esc(imageData.upload_id)}">
        <div class="image-wrapper ${type === "backdrop" ? "backdrop" : ""}">
          <img src="${safeUrl(imageData.url)}" alt="Uploaded ${type}" loading="lazy">
          <div class="image-overlay">
            <div class="select-indicator">
              <i class="fas fa-check-circle"></i>
            </div>
          </div>
        </div>
      </div>
    `;

    // Insert after upload drop zone
    uploadDrop.parentElement.insertAdjacentElement("afterend", gridItem);

    // Deselect other images
    document
      .querySelectorAll(`.image-option[data-type="${type}"]`)
      .forEach((opt) => {
        if (opt !== gridItem.querySelector(".image-option")) {
          opt.classList.remove("selected");
        }
      });

    // Update hidden input
    const selectedInput = document.getElementById(`selected_${type}`);
    if (selectedInput) {
      selectedInput.value = "";
    }

    // Add click handler to the new image option
    gridItem
      .querySelector(".image-option")
      .addEventListener("click", function (e) {
        document
          .querySelectorAll(`.image-option[data-type="${type}"]`)
          .forEach((opt) => {
            opt.classList.remove("selected");
          });
        this.classList.add("selected");

        // Update hidden input to reflect selection (will be saved on Continue)
        const selectedInput = document.getElementById(`selected_${type}`);
        if (selectedInput) {
          selectedInput.value = "";
        }
        const customInput = document.getElementById(`custom_${type}_url`);
        if (customInput) {
          customInput.value = `upload:${imageData.upload_id}`;
        }
      });

    // Reset upload drop zone to normal state (remove uploaded class)
    uploadDrop.classList.remove("uploaded");
    uploadDrop.classList.remove("drag-over");

    const uploadContent = uploadDrop.querySelector(".upload-drop-content");
    if (uploadContent) {
      const specs = getAssetSpecs();
      const typeSpec = specs[type] || {};
      uploadContent.innerHTML = `
        <i class="fas fa-cloud-upload-alt"></i>
        <p>Upload ${
          type === "poster"
            ? "Poster"
            : type === "backdrop"
            ? "Backdrop"
            : type === "still"
            ? "Still"
            : "Logo"
        }</p>
        <small>${typeSpec.label_short || ""}</small>
      `;
    }

    // Ensure drop zone is clickable - remove any blocking styles
    uploadDrop.style.pointerEvents = "auto";
    uploadDrop.style.cursor = "pointer";
    uploadDrop.style.position = "relative";
    uploadDrop.style.zIndex = "1";

    console.log(`[Customize] Reset drop zone for ${type}`, {
      classList: uploadDrop.classList.toString(),
      pointerEvents: uploadDrop.style.pointerEvents,
      cursor: uploadDrop.style.cursor,
    });
  }

  /**
   * Setup horizontal scroll with mouse wheel for image grids
   */
  function setupHorizontalScroll() {
    // Use setTimeout to ensure image grids are loaded (they're dynamically populated)
    setTimeout(() => {
      const imageGrids = document.querySelectorAll(".image-grid");

      imageGrids.forEach((grid) => {
        // Enable horizontal scrolling with mouse wheel
        grid.addEventListener(
          "wheel",
          function (e) {
            // Only handle horizontal scroll if:
            // 1. Shift key is not pressed (shift+wheel is typically zoom)
            // 2. The container has horizontal scroll (scrollWidth > clientWidth)
            if (!e.shiftKey && this.scrollWidth > this.clientWidth) {
              // Prevent default vertical scroll
              e.preventDefault();

              // Scroll horizontally
              // Use deltaY (vertical scroll) to scroll horizontally
              this.scrollLeft += e.deltaY;
            }
          },
          { passive: false }
        ); // passive: false allows preventDefault()

        console.log("[Customize] Horizontal scroll enabled for grid:", grid);
      });

      console.log(
        `[Customize] Horizontal scroll setup complete for ${imageGrids.length} grids`
      );
    }, 500); // Delay to ensure grids are rendered
  }

  /**
   * Setup navigation buttons
   */
  function setupNavigation() {
    const backButton = document.getElementById("back-button");
    const skipButton = document.getElementById("skip-button");

    if (backButton) {
      backButton.addEventListener("click", () => {
        sessionManager.navigateWithSession(`${getApplyBasePath()}/upload`);
      });
    }

    if (skipButton) {
      skipButton.addEventListener("click", () => {
        // Save empty data for this step
        sessionManager.saveStepData(3, {
          skipped: true,
          selected_poster: "",
          selected_backdrop: "",
          custom_poster_url: "",
          custom_backdrop_url: "",
        });

        // Navigate to distribution
        sessionManager.navigateWithSession(
          `${getApplyBasePath()}/distribution`
        );
      });
    }
  }

  /**
   * Setup form submission
   */
  function setupFormSubmission() {
    const submitButton = document.getElementById("customize-submit");
    if (!submitButton) return;

    // Prevent duplicate event listener attachment
    if (submitButton.dataset.initialized === "true") {
      console.log("[Customize] Submit button already initialized, skipping");
      return;
    }
    submitButton.dataset.initialized = "true";

    let isSubmitting = false;

    submitButton.addEventListener("click", async function (e) {
      // Prevent double-click
      if (isSubmitting) {
        console.log(
          "[Customize] ⚠️ Already submitting, ignoring duplicate click"
        );
        return;
      }

      isSubmitting = true;
      console.log("[Customize] 🚀 Starting submission...");

      const form = document.getElementById("customizeForm");
      const formData = new FormData(form);

      // Get selected or uploaded images
      const selectedPoster =
        formData.get("selected_poster") || formData.get("custom_poster_url");
      const selectedBackdrop =
        formData.get("selected_backdrop") ||
        formData.get("custom_backdrop_url");
      const selectedStill =
        formData.get("selected_still") || formData.get("custom_still_url");
      const selectedLogo =
        formData.get("selected_logo") || formData.get("custom_logo_url");

      // Validate: Require ALL THREE - Poster, Banner, AND Backdrop/Hero
      const hasPoster = selectedPoster && selectedPoster.trim().length > 0;
      const hasBackdrop =
        selectedBackdrop && selectedBackdrop.trim().length > 0;
      const hasStill = selectedStill && selectedStill.trim().length > 0;

      console.log("[Customize] Image validation:");
      console.log("  - Has poster?", hasPoster, selectedPoster);
      console.log("  - Has backdrop/banner?", hasBackdrop, selectedBackdrop);
      console.log("  - Has backdrop/hero?", hasStill, selectedStill);

      // Check which images are missing
      const missingImages = [];
      if (!hasPoster) missingImages.push("Poster");
      if (!hasBackdrop) missingImages.push("Banner");
      if (!hasStill) missingImages.push("Backdrop/Hero");

      if (missingImages.length > 0) {
        console.error(
          "[Customize] Validation FAILED - Missing required images:",
          missingImages
        );

        if (missingImages.length === 3) {
          showNotification(
            "Please select a Poster, Banner, and Backdrop/Hero image.",
            "warning"
          );
        } else if (missingImages.length === 2) {
          showNotification(
            `Please select a ${missingImages[0]} and ${missingImages[1]} image.`,
            "warning"
          );
        } else {
          showNotification(
            `Please select a ${missingImages[0]} image.`,
            "warning"
          );
        }

        isSubmitting = false;
        return;
      }

      console.log("[Customize] Validation PASSED ✓");
      console.log("[Customize] Saving step data...");
      console.log("[Customize] uploadedImages object:", uploadedImages);
      console.log(
        "[Customize] uploadedImages.backdrop:",
        uploadedImages.backdrop
      );
      console.log("[Customize] uploadedImages.still:", uploadedImages.still);
      console.log("[Customize] uploadedImages.poster:", uploadedImages.poster);
      console.log(
        "[Customize] uploadedImages.treatment:",
        uploadedImages.treatment
      );
      console.log("[Customize] uploadedImages.logo:", uploadedImages.logo);

      // Get existing step data (to preserve any data saved by saveImageWithoutCropping)
      const existingData = sessionManager.getStepData(3) || {};
      console.log("[Customize] Existing step 3 data:", existingData);
      console.log("[Customize] Checking treatment preservation:");
      console.log(
        "  - existingData.custom_treatment_url:",
        existingData.custom_treatment_url
      );
      console.log(
        "  - existingData.custom_treatment_data:",
        existingData.custom_treatment_data
      );
      console.log("  - uploadedImages.treatment:", uploadedImages.treatment);
      console.log(
        '  - formData.get("custom_treatment_url"):',
        formData.get("custom_treatment_url")
      );

      // Save to session - merge with existing data
      // IMPORTANT: Don't overwrite existing custom_*_data with undefined values
      const stepData = {
        ...existingData, // Preserve all existing data
        selected_poster: selectedPoster,
        selected_backdrop: selectedBackdrop,
        selected_still: selectedStill,
        selected_logo: selectedLogo,
        custom_poster_url:
          formData.get("custom_poster_url") ||
          existingData.custom_poster_url ||
          "",
        custom_backdrop_url:
          formData.get("custom_backdrop_url") ||
          existingData.custom_backdrop_url ||
          "",
        custom_still_url:
          formData.get("custom_still_url") ||
          existingData.custom_still_url ||
          "",
        custom_treatment_url:
          formData.get("custom_treatment_url") ||
          existingData.custom_treatment_url ||
          "",
        custom_logo_url:
          formData.get("custom_logo_url") || existingData.custom_logo_url || "",
      };

      // Only set custom_*_data if we have actual data (prevents overwriting with undefined)
      if (uploadedImages.poster || existingData.custom_poster_data) {
        stepData.custom_poster_data =
          uploadedImages.poster || existingData.custom_poster_data;
      }
      if (uploadedImages.backdrop || existingData.custom_backdrop_data) {
        stepData.custom_backdrop_data =
          uploadedImages.backdrop || existingData.custom_backdrop_data;
      }
      if (uploadedImages.still || existingData.custom_still_data) {
        stepData.custom_still_data =
          uploadedImages.still || existingData.custom_still_data;
      }
      if (uploadedImages.treatment || existingData.custom_treatment_data) {
        stepData.custom_treatment_data =
          uploadedImages.treatment || existingData.custom_treatment_data;
      }
      if (uploadedImages.logo || existingData.custom_logo_data) {
        stepData.custom_logo_data =
          uploadedImages.logo || existingData.custom_logo_data;
      }

      console.log("[Customize] Step data being saved:", stepData);
      sessionManager.saveStepData(3, stepData);

      try {
        // WAIT for persistence to complete
        await persistStepData(3, stepData);

        console.log("[Customize] ✅ Data saved and persisted, navigating...");

        // Navigate to distribution page ONLY after successful persistence
        sessionManager.navigateWithSession(
          `${getApplyBasePath()}/distribution`
        );
      } catch (error) {
        console.error("[Step 3] Failed to persist to database:", error);
        showNotification(
          "Failed to save your data. Please try again.",
          "error"
        );

        // Reset submitting flag so user can retry
        isSubmitting = false;
        // Don't navigate - let user retry
      }
    });
  }

  /**
   * Show notification message
   *
   * @param {string} message - Notification message
   * @param {string} type - Notification type (success, error, info, warning)
   */
  function showNotification(message, type) {
    // Create notification element
    const notification = document.createElement("div");
    notification.className = `callout ${type} has-icon`;
    notification.style.cssText =
      "position: fixed; top: 20px; right: 20px; z-index: 9999; max-width: 400px; animation: slideIn 0.3s ease;";
    notification.innerHTML = `
      <div class="callout-icon">
        <i class="fas fa-${
          type === "success"
            ? "check-circle"
            : type === "error"
            ? "exclamation-circle"
            : "info-circle"
        }"></i>
      </div>
      <div class="callout-content">
        <p>${esc(message)}</p>
      </div>
    `;

    document.body.appendChild(notification);

    // Auto-remove after 5 seconds
    setTimeout(() => {
      notification.style.animation = "slideOut 0.3s ease";
      setTimeout(() => {
        notification.remove();
      }, 300);
    }, 5000);
  }

  /**
   * Persist step data to database
   */
  async function persistStepData(stepNumber, formData) {
    try {
      const response = await fetch("/api/submissions/save-step", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          uuid: sessionManager.getSessionId(),
          step: stepNumber,
          form_data: formData,
        }),
      });

      const result = await response.json();

      if (!result.success) {
        console.error(
          "[Step " + stepNumber + "] Database persistence failed:",
          result.message
        );
        return false;
      }

      console.log(
        "[Step " + stepNumber + "] Successfully persisted to database"
      );
      return true;
    } catch (error) {
      console.error(
        "[Step " + stepNumber + "] Error persisting to database:",
        error
      );
      throw error;
    }
  }

  // Initialize on DOM ready
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
